import React, { useState, useEffect } from 'react';
import { V8Login } from '../../types/admin';
import { listV8Logins } from '../../utils/admin/accountsApi';
import './AdminStyles.css';

interface Account {
  id?: number;
  // WhatsApp
  phone?: string;
  phone_id?: string;
  bm_id?: string;
  bm_level?: string;
  // Instagram
  page_id?: string;
  instagram_account_id?: string;
  // V8 / suporte (Instagram)
  v8_id?: number;
  support_number?: string;
  support_number_premium?: string;
  // comuns
  proxy_url?: string;
  is_active?: boolean;
  // Só WA: permite/bloqueia disparos do número.
  allow_shots?: boolean;
}

interface AccountFormProps {
  type: 'wa' | 'ig';
  account?: Account;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const AccountForm: React.FC<AccountFormProps> = ({
  type,
  account,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const isEditing = !!account;

  const [formData, setFormData] = useState({
    // WhatsApp
    phone: account?.phone || '',
    phone_id: account?.phone_id || '',
    token: '',
    bm_id: account?.bm_id || '',
    bm_level: account?.bm_level || '',
    // Instagram
    page_id: account?.page_id || '',
    instagram_account_id: account?.instagram_account_id || '',
    access_token: '',
    // V8 / suporte (Instagram)
    v8_id: account?.v8_id ? String(account.v8_id) : '',
    support_number: account?.support_number || '',
    support_number_premium: account?.support_number_premium || '',
    // comuns
    proxy_url: account?.proxy_url || '',
    is_active: account?.is_active ?? true,
    allow_shots: account?.allow_shots ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showTokens, setShowTokens] = useState(false);
  const [v8Logins, setV8Logins] = useState<V8Login[]>([]);

  // Carrega os V8 logins para o select do Instagram (igual ao WhatsApp oficial, a página precisa de um V8).
  useEffect(() => {
    if (type !== 'ig') return;
    let active = true;
    listV8Logins()
      .then(list => { if (active) setV8Logins(list); })
      .catch(() => { if (active) setV8Logins([]); });
    return () => { active = false; };
  }, [type]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (type === 'wa') {
      if (!isEditing) {
        if (!formData.phone.trim()) {
          newErrors.phone = 'Número de telefone é obrigatório';
        } else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
          newErrors.phone = 'Número de telefone inválido (10-15 dígitos)';
        }
        if (!formData.phone_id.trim()) {
          newErrors.phone_id = 'ID do telefone (phone_id) é obrigatório';
        }
        if (!formData.token.trim()) {
          newErrors.token = 'Token de acesso é obrigatório';
        } else if (formData.token.length < 20) {
          newErrors.token = 'Token muito curto';
        }
      } else if (formData.token.trim() && formData.token.length < 20) {
        newErrors.token = 'Token muito curto';
      }
    } else {
      if (!isEditing) {
        if (!formData.page_id.trim()) {
          newErrors.page_id = 'ID da página é obrigatório';
        }
        if (!formData.instagram_account_id.trim()) {
          newErrors.instagram_account_id = 'ID da conta Instagram é obrigatório';
        }
        if (!formData.access_token.trim()) {
          newErrors.access_token = 'Token de acesso é obrigatório';
        } else if (formData.access_token.length < 20) {
          newErrors.access_token = 'Token muito curto';
        }
      } else {
        if (formData.access_token.trim() && formData.access_token.length < 20) {
          newErrors.access_token = 'Token muito curto';
        }
      }

      if (!formData.v8_id) {
        newErrors.v8_id = 'Selecione um V8 para atrelar à página';
      }

      const igSupportFields: Array<'support_number' | 'support_number_premium'> = ['support_number', 'support_number_premium'];
      for (const f of igSupportFields) {
        const v = formData[f];
        if (v && v.trim() && !/^\d{10,15}$/.test(v.replace(/\D/g, ''))) {
          newErrors[f] = 'Número inválido (10-15 dígitos)';
        }
      }
    }

    if (formData.proxy_url && formData.proxy_url.trim()) {
      try {
        new URL(formData.proxy_url);
      } catch {
        newErrors.proxy_url = 'URL do proxy inválida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type: inputType } = e.target;
    const finalValue = inputType === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Monta o payload conforme o tipo e se é criação ou edição (mandando só o que o backend aceita).
  const buildPayload = () => {
    if (type === 'wa') {
      if (!isEditing) {
        return {
          phone: formData.phone.replace(/\D/g, ''),
          phone_id: formData.phone_id.trim(),
          token: formData.token,
          proxy_url: formData.proxy_url.trim() || undefined,
          is_active: formData.is_active,
          allow_shots: formData.allow_shots,
          bm_id: formData.bm_id.trim() || undefined,
          bm_level: formData.bm_level || undefined,
        };
      }
      const payload: any = {
        is_active: formData.is_active,
        allow_shots: formData.allow_shots,
        proxy_url: formData.proxy_url.trim() ? formData.proxy_url.trim() : null,
        bm_id: formData.bm_id.trim() || undefined,
        bm_level: formData.bm_level || undefined,
      };
      if (formData.token.trim()) payload.token = formData.token;
      return payload;
    }

    if (!isEditing) {
      return {
        page_id: formData.page_id.trim(),
        instagram_account_id: formData.instagram_account_id.trim(),
        access_token: formData.access_token,
        v8_id: Number(formData.v8_id),
        support_number: formData.support_number.trim() ? formData.support_number.replace(/\D/g, '') : undefined,
        support_number_premium: formData.support_number_premium.trim() ? formData.support_number_premium.replace(/\D/g, '') : undefined,
        proxy_url: formData.proxy_url.trim() || undefined,
        is_active: formData.is_active,
      };
    }
    const payload: any = {
      is_active: formData.is_active,
      proxy_url: formData.proxy_url.trim() ? formData.proxy_url.trim() : null,
      support_number: formData.support_number.trim() ? formData.support_number.replace(/\D/g, '') : null,
      support_number_premium: formData.support_number_premium.trim() ? formData.support_number_premium.replace(/\D/g, '') : null,
    };
    if (formData.access_token.trim()) payload.access_token = formData.access_token;
    if (formData.v8_id) payload.v8_id = Number(formData.v8_id);
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onSubmit(buildPayload());
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
    }
  };

  return (
    <form className="account-form" onSubmit={handleSubmit}>
      <h3>{account ? 'Editar Conta' : 'Adicionar Conta'} {type === 'wa' ? 'WhatsApp' : 'Instagram'}</h3>

      {type === 'wa' ? (
        <>
          <div className="form-group">
            <label htmlFor="phone">Número de Telefone *</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Ex: 551199999999"
              disabled={loading || isEditing}
              maxLength={15}
            />
            {errors.phone && <span className="error">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone_id">ID do Telefone (phone_id) *</label>
            <input
              id="phone_id"
              type="text"
              name="phone_id"
              value={formData.phone_id}
              onChange={handleChange}
              placeholder="Ex: 123456789012345"
              disabled={loading || isEditing}
              maxLength={255}
            />
            {errors.phone_id && <span className="error">{errors.phone_id}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="bm_id">Business Manager ID (Opcional)</label>
            <input
              id="bm_id"
              type="text"
              name="bm_id"
              value={formData.bm_id}
              onChange={handleChange}
              placeholder="Ex: 123456789"
              disabled={loading}
              maxLength={255}
            />
          </div>

          <div className="form-group">
            <label htmlFor="bm_level">Nível BM (Opcional)</label>
            <select
              id="bm_level"
              name="bm_level"
              value={formData.bm_level}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">—</option>
              <option value="bronze">Bronze</option>
              <option value="diamond">Diamond</option>
            </select>
          </div>
        </>
      ) : (
        <>
          <div className="form-group">
            <label htmlFor="page_id">ID da Página *</label>
            <input
              id="page_id"
              type="text"
              name="page_id"
              value={formData.page_id}
              onChange={handleChange}
              placeholder="Ex: 123456789"
              disabled={loading || isEditing}
              maxLength={50}
            />
            {errors.page_id && <span className="error">{errors.page_id}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="instagram_account_id">ID da Conta Instagram *</label>
            <input
              id="instagram_account_id"
              type="text"
              name="instagram_account_id"
              value={formData.instagram_account_id}
              onChange={handleChange}
              placeholder="Ex: 17841400000000000"
              disabled={loading || isEditing}
              maxLength={255}
            />
            {errors.instagram_account_id && <span className="error">{errors.instagram_account_id}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="v8_id">V8 atrelado à página *</label>
            <select
              id="v8_id"
              name="v8_id"
              value={formData.v8_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">— Selecione um V8 —</option>
              {v8Logins.map(v8 => (
                <option key={v8.id} value={String(v8.id)}>{v8.login} (#{v8.id})</option>
              ))}
            </select>
            {errors.v8_id && <span className="error">{errors.v8_id}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="support_number">Número de Suporte (Opcional)</label>
            <input
              id="support_number"
              type="tel"
              name="support_number"
              value={formData.support_number}
              onChange={handleChange}
              placeholder="Ex: 5549999999999"
              disabled={loading}
              maxLength={15}
            />
            {errors.support_number && <span className="error">{errors.support_number}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="support_number_premium">Número de Suporte Premium (Opcional)</label>
            <input
              id="support_number_premium"
              type="tel"
              name="support_number_premium"
              value={formData.support_number_premium}
              onChange={handleChange}
              placeholder="Ex: 5549999999999"
              disabled={loading}
              maxLength={15}
            />
            {errors.support_number_premium && <span className="error">{errors.support_number_premium}</span>}
          </div>
        </>
      )}

      {/* Tokens secretos. Em edição, deixe em branco para manter o valor atual. */}
      {type === 'wa' ? (
        <div className="form-group">
          <label htmlFor="token">Token de Acesso {isEditing ? '(deixe em branco para manter)' : '*'}</label>
          <input
            id="token"
            type={showTokens ? 'text' : 'password'}
            name="token"
            value={formData.token}
            onChange={handleChange}
            placeholder={isEditing ? '••••••••' : 'Cole seu token de acesso'}
            disabled={loading}
          />
          {errors.token && <span className="error">{errors.token}</span>}
        </div>
      ) : (
        <>
          <div className="form-group">
            <label htmlFor="access_token">Token de Acesso {isEditing ? '(deixe em branco para manter)' : '*'}</label>
            <input
              id="access_token"
              type={showTokens ? 'text' : 'password'}
              name="access_token"
              value={formData.access_token}
              onChange={handleChange}
              placeholder={isEditing ? '••••••••' : 'Cole seu token de acesso'}
              disabled={loading}
            />
            {errors.access_token && <span className="error">{errors.access_token}</span>}
          </div>
        </>
      )}

      <div className="form-group checkbox">
        <input
          id="showTokens"
          type="checkbox"
          checked={showTokens}
          onChange={(e) => setShowTokens(e.target.checked)}
          disabled={loading}
        />
        <label htmlFor="showTokens">Mostrar tokens</label>
      </div>

      <div className="form-group">
        <label htmlFor="proxy_url">URL do Proxy (Opcional)</label>
        <input
          id="proxy_url"
          type="text"
          name="proxy_url"
          value={formData.proxy_url}
          onChange={handleChange}
          placeholder="Ex: https://proxy.example.com:8080"
          disabled={loading}
          maxLength={1000}
        />
        {errors.proxy_url && <span className="error">{errors.proxy_url}</span>}
      </div>

      <div className="form-group checkbox">
        <input
          id="is_active"
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          disabled={loading}
        />
        <label htmlFor="is_active">Ativo</label>
      </div>

      {type === 'wa' && (
        <div className="form-group checkbox">
          <input
            id="allow_shots"
            type="checkbox"
            name="allow_shots"
            checked={formData.allow_shots}
            onChange={handleChange}
            disabled={loading}
          />
          <label htmlFor="allow_shots">Permitir disparos</label>
        </div>
      )}

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Salvando...' : (account ? 'Atualizar' : 'Criar')}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </button>
      </div>
    </form>
  );
};
